// src/components/ClassComments.js
// Collapsible post-class comment thread (class guestbook)
// Shows teaser (count + preview) when collapsed; full thread when expanded
// Supports posting, admin deletion, pagination

import React, { useState, useEffect, useRef } from 'react';
import AvatarPhoto from './AvatarPhoto';
import MemberCardTrigger from './MemberCardTrigger';
import { useMemberPhotos } from '../services/memberProfiles';
import { postComment, getComments, deleteComment, editComment, subscribeToComments, getCommentsByIds } from '../services/classComments';
import { notifyCoachReply, removeCoachReplyNotification, notifyNewComment } from '../services/commentNotifications';
import { markDraftAnswered } from '../services/replyAssistant';
import { getCachedSiteContent } from '../services/firebase';
import ClassCommentsV2 from './ClassCommentsV2';

// ── Relative time helper ──
function timeAgo(isoString) {
    if (!isoString) return '';
    const diff = Date.now() - new Date(isoString).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins}m ago`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    if (days < 7) return `${days}d ago`;
    return new Date(isoString).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function ClassCommentsLegacy({ classId, recordingId, userId, userName, isAdmin, commentCount = 0, defaultExpanded = false, getToken, highlightCommentId = null }) {
    const [expanded, setExpanded] = useState(defaultExpanded);
    const [comments, setComments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [lastDoc, setLastDoc] = useState(null);
    const [hasMore, setHasMore] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);
    const [text, setText] = useState('');
    const [posting, setPosting] = useState(false);
    const [postError, setPostError] = useState(false);
    const [deletingId, setDeletingId] = useState(null);
    // Inline edit state. editingId is the comment id currently being edited (null = none).
    // Kept separate from the composer/reply inputs so editing never disturbs an
    // in-progress new comment or reply draft.
    const [editingId, setEditingId] = useState(null);
    const [editText, setEditText] = useState('');
    const [editSaving, setEditSaving] = useState(false);
    const [editError, setEditError] = useState(false);
    // Deep-link highlight (from a tapped reply notification): expand the thread +
    // scroll to + briefly ring the target comment. Best-effort — a complete no-op
    // when highlightCommentId is absent or the comment isn't in the loaded window.
    const [highlightedId, setHighlightedId] = useState(null);
    // Member photos are LOOKED UP by userId (adr-member-profile-photos) — the
    // comment docs themselves are unchanged. No photo ⇒ the initials circle as before.
    const photoFor = useMemberPhotos([userId, ...comments.map((cm) => cm.userId)]);
    const highlightContainerRef = useRef(null);
    const highlightDoneForRef = useRef(null);
    // Force the thread open when arriving via a notification deep-link.
    useEffect(() => {
        if (highlightCommentId) setExpanded(true);
    }, [highlightCommentId]);
    // Once the target reply is present in the loaded list, scroll to it and ring it
    // for ~2.8s. Guarded by a ref so it runs at most once per target id.
    useEffect(() => {
        if (!highlightCommentId || loading) return undefined;
        if (highlightDoneForRef.current === highlightCommentId) return undefined;
        if (!comments.some((c) => c.id === highlightCommentId)) return undefined; // not in window — best-effort no-op
        highlightDoneForRef.current = highlightCommentId;
        const scrollT = setTimeout(() => {
            try {
                const el = highlightContainerRef.current
                    && highlightContainerRef.current.querySelector(`[data-comment-id="${highlightCommentId}"]`);
                if (el && el.scrollIntoView) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
            } catch (_) { /* noop */ }
            setHighlightedId(highlightCommentId);
        }, 150);
        const clearT = setTimeout(() => setHighlightedId(null), 2800);
        return () => { clearTimeout(scrollT); clearTimeout(clearT); };
    }, [highlightCommentId, loading, comments]);
    // Admin-reply state. replyingToId is the parent comment id we're composing a reply to;
    // null means no reply input is open. Reply text/posting/error are kept separate from
    // the main comment input so the two flows don't clobber each other if admin opens a
    // reply while there's text in the top-level input.
    const [replyingToId, setReplyingToId] = useState(null);
    const [replyText, setReplyText] = useState('');
    const [replyPosting, setReplyPosting] = useState(false);
    const [replyError, setReplyError] = useState(false);
    // Brief "Posted ✓" pill shown after a successful reply lands. The reply input
    // stays open so admin can keep replying or close it manually — solves the
    // "I didn't see my reply land" → admin posts again → duplicate replies pattern
    // observed in production.
    const [replyJustPosted, setReplyJustPosted] = useState(false);
    const replyPostedTimerRef = useRef(null);

    // Reset reply input state when the target (class/recording) changes, so an
    // open reply input doesn't leak across modal opens. Also clears the "Posted ✓"
    // pill timer if it was pending.
    useEffect(() => {
        setReplyingToId(null);
        setReplyText('');
        setReplyError(false);
        setReplyJustPosted(false);
        if (replyPostedTimerRef.current) {
            clearTimeout(replyPostedTimerRef.current);
            replyPostedTimerRef.current = null;
        }
    }, [classId, recordingId]);

    // Clear the "Posted ✓" timer on unmount so it doesn't fire into a dead component.
    useEffect(() => () => {
        if (replyPostedTimerRef.current) {
            clearTimeout(replyPostedTimerRef.current);
            replyPostedTimerRef.current = null;
        }
    }, []);

    // Backfill bookkeeping — parents fetched outside the newest-N subscription window
    // because a reply to them landed in the window. See the backfill useEffect below.
    // - backfilledIdsRef: parent ids we've already attempted to fetch (avoids re-fetching
    //   on every snapshot). Reset when the subscription target changes.
    // - currentTargetRef: identifier for the active (classId, recordingId) pair so we can
    //   discard backfill responses that resolved after the user moved to a different class.
    const backfilledIdsRef = useRef(new Set());
    const currentTargetRef = useRef(null);

    // Effective comment count — excludes "_backfilled" and "_preservedForReply"
    // parents because they're already counted in the parent doc's atomic-incremented
    // `commentCount`. Including them would double-count when the lagging-commentCount
    // fallback (`Math.max(commentCount, length)`) tips toward `length`.
    const effectiveCommentLength = comments.reduce(
        (acc, c) => acc + (c._backfilled || c._preservedForReply ? 0 : 1),
        0
    );

    // Display count:
    // - While still loading the first snapshot, trust the parent's `commentCount` prop so the
    //   collapsed header doesn't flash "Comments" (0) before live data arrives.
    // - Once loaded, if the snapshot contains every comment (hasMore=false), `effectiveCommentLength`
    //   is the source of truth — it reflects optimistic posts, remote posts, and remote deletes
    //   correctly in real time.
    // - When there are more than `pageSize` total, fall back to `commentCount` (kept live by the
    //   atomic increment on the parent doc) so we don't undercount. Use Math.max so optimistic
    //   posts still show instantly even before the parent doc's increment propagates.
    const displayCount = loading
        ? commentCount
        : hasMore
            ? Math.max(commentCount, effectiveCommentLength)
            : effectiveCommentLength;

    // Derive user initials from userName (e.g. "Sarah B." → "SB")
    const userInitials = userName
        ? userName.split(' ').map((n) => n.charAt(0).toUpperCase()).slice(0, 2).join('')
        : '?';

    // Subscribe to comments in real-time. Drives both the live thread (when expanded) and
    // the collapsed-state preview, so User B sees User A's comment instantly without refreshing,
    // and the "most recent comment" teaser is always current.
    //
    // Optimistic reconciliation: when a comment is posted, we add a temp entry (id prefixed
    // with 'temp-') for instant visual feedback. When the real doc arrives in the snapshot,
    // we drop temps whose (userId + text) have been confirmed — the real doc replaces them
    // in the list seamlessly. Failed posts still get removed explicitly in handlePost.
    //
    // Backfilled-parent preservation: parents fetched by the backfill effect (older than the
    // newest-N window) are also preserved across snapshots — they only get dropped if they
    // appear in the new snapshot itself (meaning they've moved INTO the window, e.g. via a
    // new reply landing or admin scroll). The backfill effect repopulates as needed.
    useEffect(() => {
        if (!classId && !recordingId) return;
        // Reset backfill bookkeeping on target change so a new class doesn't reuse the
        // previous class's backfill cache.
        const target = `${classId || ''}-${recordingId || ''}`;
        currentTargetRef.current = target;
        backfilledIdsRef.current = new Set();

        // Admin subscriptions use a wider window (50 vs 20) because admins reply to
        // older comments far more often than regular users — the wider window
        // dramatically reduces how often a reply pushes its parent out of the
        // snapshot. Cost impact is negligible (single-admin app). "Load more"
        // pagination stays at 20-doc pages.
        const subscriptionPageSize = isAdmin ? 50 : 20;

        const unsubscribe = subscribeToComments({
            classId,
            recordingId,
            pageSize: subscriptionPageSize,
            onUpdate: ({ comments: nextComments, lastDoc: nextLastDoc, hasMore: nextHasMore }) => {
                setComments((prev) => {
                    // Preserve optimistic temps that haven't been confirmed yet.
                    // Match key includes parentCommentId so two simultaneous optimistic
                    // posts (e.g. admin replies "Thanks!" to two different comments) don't
                    // collapse into a single confirmed doc — each temp is matched to its
                    // own parent thread.
                    const temps = prev.filter((c) => typeof c.id === 'string' && c.id.startsWith('temp-'));
                    const unconfirmedTemps = temps.filter((t) => !nextComments.some(
                        (n) => n.userId === t.userId && n.text === t.text && (n.parentCommentId || null) === (t.parentCommentId || null)
                    ));
                    // Preserve backfilled parents that aren't already in the new snapshot.
                    // (If a backfilled parent shows up in the snapshot, we drop our copy and
                    // let the snapshot version take over — same id, no functional difference,
                    // but the snapshot one isn't flagged `_backfilled` so it counts properly.)
                    const nextIds = new Set(nextComments.map((c) => c.id));
                    const stillBackfilled = prev.filter((c) => c._backfilled && !nextIds.has(c.id));

                    // Preserve "edge of window" parents whose replies just pushed them
                    // out of newest-N. When admin replies to a comment at position ~N,
                    // the new reply takes a slot in the snapshot and the parent drops
                    // out — without this preservation, the reply orphans into "Earlier
                    // conversation" until the backfill effect fetches the parent back,
                    // causing a visible flicker where admin can't see the reply they
                    // just posted (and often double-replies as a result).
                    //
                    // Conditions for preservation:
                    //   - it's a top-level comment from prev (parentCommentId falsy)
                    //   - it's not already in the new snapshot
                    //   - it's not already being preserved via _backfilled
                    //   - at least one reply OR unconfirmed-reply-temp in the new state
                    //     still points at it
                    //
                    // When the reply is later deleted/orphaned/moved, the next reconciliation
                    // will find no pointer and drop the preserved parent naturally.
                    const replyParentIds = new Set();
                    nextComments.forEach((c) => {
                        if (c.parentCommentId) replyParentIds.add(c.parentCommentId);
                    });
                    unconfirmedTemps.forEach((t) => {
                        if (t.parentCommentId) replyParentIds.add(t.parentCommentId);
                    });
                    const preservedForReply = prev
                        .filter((c) =>
                            !c.parentCommentId
                            && !c._backfilled
                            && !nextIds.has(c.id)
                            && replyParentIds.has(c.id)
                        )
                        .map((c) => ({ ...c, _preservedForReply: true }));

                    return [...unconfirmedTemps, ...nextComments, ...stillBackfilled, ...preservedForReply];
                });
                setLastDoc(nextLastDoc);
                setHasMore(nextHasMore);
                setLoading(false);
            },
        });
        return () => unsubscribe();
    }, [classId, recordingId, isAdmin]);

    // Backfill missing parents — when a reply lands in the subscription window but its
    // parent doesn't (parent is older than the newest-N), fetch the parent so the thread
    // groups correctly under the right comment instead of orphaning the reply visually
    // beneath the last loaded top-level comment.
    //
    // Without this, admin reply bursts (Danielle catching up on a backlog) make her recent
    // replies appear stacked under whichever loaded top-level happens to be last — the
    // 42px indent + ↳ visual treatment makes them look like replies to that wrong parent.
    //
    // Runs whenever `comments` changes; the `backfilledIdsRef` skip-list ensures we only
    // fetch each missing parent once. Stale-target check on resolve prevents a slow
    // backfill from polluting state if the user navigates to a different class mid-fetch.
    useEffect(() => {
        if (!classId && !recordingId) return;
        if (comments.length === 0) return;

        const loadedIds = new Set(comments.map((c) => c.id));
        const missingParentIds = [];
        comments.forEach((c) => {
            if (
                c.parentCommentId
                && !loadedIds.has(c.parentCommentId)
                && !backfilledIdsRef.current.has(c.parentCommentId)
            ) {
                missingParentIds.push(c.parentCommentId);
            }
        });
        if (missingParentIds.length === 0) return;

        // Mark as in-flight BEFORE await so the next render doesn't re-trigger this fetch.
        const idsToFetch = Array.from(new Set(missingParentIds));
        idsToFetch.forEach((id) => backfilledIdsRef.current.add(id));

        const target = currentTargetRef.current;
        let cancelled = false;
        getCommentsByIds(idsToFetch)
            .then((parents) => {
                if (cancelled) return;
                // Stale check — user may have switched classes mid-fetch.
                if (currentTargetRef.current !== target) return;
                if (!parents || parents.length === 0) return;
                setComments((prev) => {
                    const existing = new Set(prev.map((c) => c.id));
                    const additions = parents
                        .filter((p) => !existing.has(p.id))
                        .map((p) => ({ ...p, _backfilled: true }));
                    if (additions.length === 0) return prev;
                    // Re-sort backfilled chunk desc by createdAt so the rendered top-level
                    // list stays roughly chronological after multiple backfill rounds.
                    const nonBackfilled = prev.filter((c) => !c._backfilled);
                    const allBackfilled = [
                        ...prev.filter((c) => c._backfilled),
                        ...additions,
                    ].sort((a, b) => (b.createdAt || '').localeCompare(a.createdAt || ''));
                    return [...nonBackfilled, ...allBackfilled];
                });
            })
            .catch((err) => {
                console.warn('Comment parent backfill failed:', err.message);
                // Roll back the in-flight markers so a future render can retry.
                idsToFetch.forEach((id) => backfilledIdsRef.current.delete(id));
            });

        return () => { cancelled = true; };
    }, [comments, classId, recordingId]);

    // Load more (pagination).
    // Dedupes against any already-present ids — a parent that was previously backfilled
    // or preserved (because a reply to it was in the live window) may be re-returned by
    // natural pagination once the cursor walks back far enough. We drop the old flagged
    // copy and let the natural-pagination version take over so it counts toward the
    // displayCount correctly.
    const handleLoadMore = async () => {
        if (!hasMore || loadingMore) return;
        setLoadingMore(true);
        const result = await getComments({ classId, recordingId, pageSize: 20, lastDoc });
        setComments((prev) => {
            const incomingIds = new Set(result.comments.map((c) => c.id));
            // Strip any flagged parent that's about to be replaced by its real
            // pagination entry (avoid duplicate React keys + double-counting).
            const cleaned = prev.filter((c) => !((c._backfilled || c._preservedForReply) && incomingIds.has(c.id)));
            const presentIds = new Set(cleaned.map((c) => c.id));
            const additions = result.comments.filter((c) => !presentIds.has(c.id));
            return [...cleaned, ...additions];
        });
        setLastDoc(result.lastDoc);
        setHasMore(result.hasMore);
        setLoadingMore(false);
    };

    // Post a comment
    const handlePost = async () => {
        if (!text.trim() || posting || !userId) return;
        setPosting(true);
        setPostError(false);

        const newComment = {
            userId,
            userName: userName || 'Member',
            userInitials,
            isCoach: isAdmin,
            classId,
            recordingId,
            text: text.trim(),
        };

        // Optimistic: add to list immediately
        const optimistic = {
            id: 'temp-' + Date.now(),
            ...newComment,
            text: newComment.text.slice(0, 280),
            createdAt: new Date().toISOString(),
            status: 'active',
        };
        setComments((prev) => [optimistic, ...prev]);
        setText('');

        const result = await postComment(newComment);
        if (result) {
            // Replace optimistic with real — snapshot will also include this doc shortly,
            // and the subscription reconciliation strips duplicate temps, so order stays stable.
            setComments((prev) => prev.map((c) => c.id === optimistic.id ? result : c));
            // Notify the admin (Danielle) that a member commented — in-app only, no push.
            // Best-effort, fire-and-forget: must NEVER block or fail the comment post.
            // Skipped when the commenter IS the admin (no self-notify). Forward-only.
            if (!isAdmin && typeof getToken === 'function') {
                notifyNewComment({
                    classId: classId || null,
                    recordingId: recordingId || null,
                    vodAssetId: recordingId || null,
                    commentId: result.id,
                    commentText: result.text || newComment.text,
                    fromName: userName || 'A member',
                }, getToken);
            }
        } else {
            // Remove optimistic on failure
            setComments((prev) => prev.filter((c) => c.id !== optimistic.id));
            setPostError(true);
        }
        setPosting(false);
    };

    // Open the reply input below a top-level comment (admin-only via UI gating below).
    const handleStartReply = (commentId) => {
        setReplyingToId(commentId);
        setReplyText('');
        setReplyError(false);
        setReplyJustPosted(false);
        if (replyPostedTimerRef.current) {
            clearTimeout(replyPostedTimerRef.current);
            replyPostedTimerRef.current = null;
        }
    };

    // Close the reply input without posting.
    const handleCancelReply = () => {
        setReplyingToId(null);
        setReplyText('');
        setReplyError(false);
        setReplyJustPosted(false);
        if (replyPostedTimerRef.current) {
            clearTimeout(replyPostedTimerRef.current);
            replyPostedTimerRef.current = null;
        }
    };

    // Post an admin reply to a specific top-level comment. Optimistic with reconciliation —
    // the snapshot delivers the real doc and our temp gets dropped via the
    // (userId + text + parentCommentId) match in the subscribe callback above. On failure
    // we restore the typed text so admin can retry without retyping.
    const handlePostReply = async (parentCommentId) => {
        if (!replyText.trim() || replyPosting || !userId || !isAdmin) return;
        setReplyPosting(true);
        setReplyError(false);

        const newReply = {
            userId,
            userName: userName || 'Member',
            userInitials,
            isCoach: isAdmin,
            classId,
            recordingId,
            text: replyText.trim(),
            parentCommentId,
        };

        const optimistic = {
            id: 'temp-reply-' + Date.now(),
            ...newReply,
            text: newReply.text.slice(0, 280),
            createdAt: new Date().toISOString(),
            status: 'active',
        };
        setComments((prev) => [optimistic, ...prev]);
        const submittedText = newReply.text;
        setReplyText('');

        const result = await postComment(newReply);
        if (result) {
            setComments((prev) => prev.map((c) => c.id === optimistic.id ? result : c));
            // Reply Assistant: this comment is now answered — close its queue entry so
            // Admin → Replies can't offer a second reply. Fire-and-forget; no-op when
            // there is no draft doc.
            markDraftAnswered(parentCommentId);
            // Notify the parent comment's author that Danielle replied. Best-effort,
            // fire-and-forget — must NEVER block or fail the reply post. Forward-only:
            // fires once, here, at reply time (no backfill). Self-replies are skipped
            // both here and server-side. Goes through the verifyAdmin-gated endpoint.
            const parentForNotify = comments.find((c) => c.id === parentCommentId);
            if (parentForNotify && parentForNotify.userId && parentForNotify.userId !== userId && typeof getToken === 'function') {
                notifyCoachReply({
                    recipientUserId: parentForNotify.userId,
                    classId: classId || null,
                    recordingId: recordingId || null,
                    vodAssetId: recordingId || null,
                    parentCommentId,
                    parentText: parentForNotify.text || '',
                    replyCommentId: result.id,
                    replyText: result.text || submittedText,
                }, getToken);
            }
            // Keep the reply input open on success so admin sees a clear "it landed"
            // signal anchored next to the parent they replied to. The text was
            // already cleared above (line just before this); admin can post another
            // reply, hit Esc, or click × to close manually. Brief "Posted ✓" pill
            // fades out after 1.8s as a passive confirmation.
            setReplyJustPosted(true);
            if (replyPostedTimerRef.current) clearTimeout(replyPostedTimerRef.current);
            replyPostedTimerRef.current = setTimeout(() => {
                setReplyJustPosted(false);
                replyPostedTimerRef.current = null;
            }, 1800);
        } else {
            // Remove optimistic on failure, keep input open with text restored
            setComments((prev) => prev.filter((c) => c.id !== optimistic.id));
            setReplyText(submittedText);
            setReplyError(true);
        }
        setReplyPosting(false);
    };

    // Delete a comment (admin) — cascades to replies for top-level deletes.
    // Confirmation prompt names the cascade scope so admin knows what they're removing.
    // Optimistic UI removes parent + replies in one pass; subscription reflects in next snapshot.
    const handleDelete = async (comment) => {
        const isTopLevel = !comment.parentCommentId;
        const repliesToCascade = isTopLevel
            ? comments.filter((c) => c.parentCommentId === comment.id && !String(c.id).startsWith('temp-'))
            : [];
        const promptMsg = repliesToCascade.length > 0
            ? `Delete this comment and ${repliesToCascade.length} repl${repliesToCascade.length === 1 ? 'y' : 'ies'}?`
            : 'Delete this comment?';
        if (!window.confirm(promptMsg)) return;
        setDeletingId(comment.id);
        const success = await deleteComment(comment.id, classId, recordingId);
        if (success) {
            // Optimistic removal of parent + cascaded replies. The subscription will also
            // reflect status='deleted' for these in its next snapshot, keeping the list
            // consistent across viewers.
            const idsToRemove = new Set([comment.id, ...repliesToCascade.map((r) => r.id)]);
            setComments((prev) => prev.filter((c) => !idsToRemove.has(c.id)));
            // Drop any reply notifications tied to deleted replies (the reply itself if
            // a reply was deleted directly, plus any cascaded replies under a deleted
            // top-level). Best-effort — a removed reply must not leave a stale
            // "Danielle replied" notification in the recipient's center.
            if (typeof getToken === 'function') {
                const replyIdsRemoved = [];
                if (comment.parentCommentId) replyIdsRemoved.push(comment.id);
                repliesToCascade.forEach((r) => replyIdsRemoved.push(r.id));
                replyIdsRemoved.forEach((rid) => removeCoachReplyNotification(rid, getToken));
            }
            // If the reply input was open against this parent, close it too
            if (replyingToId === comment.id) handleCancelReply();
        }
        setDeletingId(null);
    };

    // ── Edit a comment (author-only via UI gating below) ──
    // Open the inline editor seeded with the current text. Closing any open reply
    // input is unnecessary (they're independent surfaces), but we DO close a
    // previously-open editor so only one row is ever in edit mode at a time.
    const handleStartEdit = (comment) => {
        setEditingId(comment.id);
        setEditText(comment.text || '');
        setEditError(false);
    };

    const handleCancelEdit = () => {
        setEditingId(null);
        setEditText('');
        setEditError(false);
    };

    // Save an edit. Optimistic: patch text + editedAt locally, then persist. On
    // failure, revert to the original text and keep the editor open so the user
    // doesn't lose their changes. No-op (just closes) when text is unchanged or
    // empty — mirrors the post path's "non-empty required" rule without ever
    // letting an edit blank out a comment.
    const handleSaveEdit = async (comment) => {
        const trimmed = editText.trim().slice(0, 280);
        if (editSaving) return;
        if (!trimmed || trimmed === (comment.text || '').trim()) { handleCancelEdit(); return; }
        setEditSaving(true);
        setEditError(false);
        const original = comment.text;
        const editedAtIso = new Date().toISOString();
        // Optimistic local patch (text + editedAt). The onSnapshot will deliver the
        // same values for in-window docs; off-window docs keep this local patch,
        // consistent with how the list already handles paginated/backfilled items.
        setComments((prev) => prev.map((c) => c.id === comment.id ? { ...c, text: trimmed, editedAt: editedAtIso } : c));
        const success = await editComment(comment.id, trimmed);
        if (success) {
            handleCancelEdit();
        } else {
            // Revert the optimistic patch; keep the editor open with the user's draft.
            setComments((prev) => prev.map((c) => c.id === comment.id ? { ...c, text: original } : c));
            setEditError(true);
        }
        setEditSaving(false);
    };

    // ── Group comments client-side for threaded rendering ──
    // Top-level: parentCommentId null/undefined. Includes backfilled parents fetched
    // by the backfill effect above — they live in `comments` like any other top-level
    // and are filtered into the same bucket here.
    // Replies: collected under their parent (sorted oldest-first within the group
    // for chronological reading order under the parent).
    // Orphaned replies: parentCommentId points to a comment we couldn't load — should
    // be vanishingly rare now that the backfill effect resolves missing parents on
    // demand. The remaining cases are: (a) parent was hard-deleted from Firestore
    // bypassing the cascade-soft-delete path, or (b) backfill is still in flight on
    // the very first render. They render below an "Earlier conversation" divider so
    // they can never visually attach to the wrong loaded parent (the original bug).
    const topLevelComments = comments.filter((c) => !c.parentCommentId);
    const topLevelIds = new Set(topLevelComments.map((c) => c.id));
    const repliesByParent = {};
    const orphanedReplies = [];
    comments.forEach((c) => {
        if (c.parentCommentId) {
            if (topLevelIds.has(c.parentCommentId)) {
                if (!repliesByParent[c.parentCommentId]) repliesByParent[c.parentCommentId] = [];
                repliesByParent[c.parentCommentId].push(c);
            } else {
                orphanedReplies.push(c);
            }
        }
    });
    Object.keys(repliesByParent).forEach((parentId) => {
        repliesByParent[parentId].sort((a, b) => (a.createdAt || '').localeCompare(b.createdAt || ''));
    });

    // Render a single comment row. `isReply` controls indent + smaller avatar/font.
    // `isLastInGroup` suppresses the bottom border on the final row so the list
    // doesn't end with a trailing divider line.
    const renderRow = ({ comment, isReply, isLastInGroup }) => (
        <div
            key={comment.id}
            data-comment-id={comment.id}
            style={{
                display: 'flex', gap: isReply ? 8 : 10, alignItems: 'flex-start',
                padding: isReply ? '10px 0' : '14px 0',
                paddingLeft: isReply ? 42 : 0,
                borderBottom: isLastInGroup ? 'none' : '1px solid var(--mom-cream-dark)',
                animation: String(comment.id).startsWith('temp-') ? 'fadeIn 0.3s ease-out' : 'none',
                background: highlightedId === comment.id ? 'color-mix(in srgb, var(--mom-primary) 12%, transparent)' : 'transparent',
                borderRadius: highlightedId === comment.id ? 10 : 0,
                transition: 'background 0.5s ease',
            }}
        >
            {/* Avatar — smaller for replies. Member card on hover/tap when the
                member has a tagline; otherwise the trigger returns the avatar as-is. */}
            <MemberCardTrigger userId={comment.isCoach ? null : comment.userId} name={comment.userName || 'Member'} display="block">
            <div style={{
                width: isReply ? 24 : 32, height: isReply ? 24 : 32, borderRadius: '50%', flexShrink: 0,
                background: comment.isCoach
                    ? 'linear-gradient(135deg, var(--mom-primary), var(--mom-primary-dark))'
                    : 'var(--mom-cream-dark)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                position: 'relative',
            }}>
                <span style={{
                    fontFamily: 'var(--mom-font-sans)', fontSize: isReply ? 10 : 12, fontWeight: 700,
                    color: comment.isCoach ? '#fff' : 'var(--mom-charcoal)',
                }}>{comment.isCoach ? 'D' : (comment.userInitials || '?')}</span>
                {!comment.isCoach && <AvatarPhoto url={photoFor(comment.userId)} />}
            </div>
            </MemberCardTrigger>
            {/* Content */}
            <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 3, flexWrap: 'wrap' }}>
                    {isReply && (
                        <span style={{
                            fontFamily: 'var(--mom-font-sans)', fontSize: 11,
                            color: 'var(--mom-warm-gray)', flexShrink: 0,
                        }}>↳</span>
                    )}
                    <span style={{
                        fontFamily: 'var(--mom-font-sans)', fontSize: isReply ? 12 : 13, fontWeight: 600,
                        color: comment.isCoach ? 'var(--mom-primary)' : 'var(--mom-charcoal)',
                    }}>
                        {comment.isCoach ? 'Danielle' : comment.userName}
                    </span>
                    {comment.isCoach && (
                        <span style={{
                            width: 6, height: 6, borderRadius: '50%',
                            background: 'var(--mom-primary)', display: 'inline-block',
                        }} />
                    )}
                    <span style={{
                        fontFamily: 'var(--mom-font-sans)', fontSize: 12,
                        color: 'var(--mom-warm-gray)',
                    }}>{timeAgo(comment.createdAt)}</span>
                    {comment.editedAt && (
                        <span
                            title={`edited ${timeAgo(comment.editedAt)}`}
                            style={{
                                fontFamily: 'var(--mom-font-sans)', fontSize: 11,
                                color: 'var(--mom-warm-gray)', opacity: 0.7, fontStyle: 'italic',
                            }}
                        >· edited</span>
                    )}
                </div>
                {editingId === comment.id ? (
                    <div>
                        <div style={{ position: 'relative' }}>
                            <input
                                type="text"
                                value={editText}
                                autoFocus
                                onChange={(e) => { setEditText(e.target.value.slice(0, 280)); setEditError(false); }}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSaveEdit(comment); }
                                    if (e.key === 'Escape') { e.preventDefault(); handleCancelEdit(); }
                                }}
                                maxLength={280}
                                style={{
                                    width: '100%', padding: isReply ? '8px 40px 8px 14px' : '10px 44px 10px 16px',
                                    background: 'var(--mom-cream)', border: 'none',
                                    borderRadius: 10, fontFamily: 'var(--mom-font-sans)',
                                    fontSize: isReply ? 13 : 14, color: 'var(--mom-charcoal)',
                                    outline: 'none', boxSizing: 'border-box',
                                    transition: 'box-shadow 0.15s ease',
                                }}
                                onFocus={(e) => { e.target.style.boxShadow = '0 0 0 2px color-mix(in srgb, var(--mom-primary) 25%, transparent)'; }}
                                onBlur={(e) => { e.target.style.boxShadow = 'none'; }}
                            />
                            {editText.trim() && (
                                <button
                                    onClick={() => handleSaveEdit(comment)}
                                    disabled={editSaving}
                                    title="Save"
                                    style={{
                                        position: 'absolute', right: 4, top: '50%', transform: 'translateY(-50%)',
                                        width: 28, height: 28, borderRadius: '50%',
                                        background: 'var(--mom-primary)', border: 'none',
                                        cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        opacity: editSaving ? 0.5 : 1,
                                    }}
                                >
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                        <polyline points="20 6 9 17 4 12" />
                                    </svg>
                                </button>
                            )}
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 6 }}>
                            <button
                                onClick={handleCancelEdit}
                                style={{
                                    background: 'none', border: 'none', cursor: 'pointer', padding: 0,
                                    fontFamily: 'var(--mom-font-sans)', fontSize: 11, fontWeight: 600,
                                    color: 'var(--mom-warm-gray)',
                                }}
                            >Cancel</button>
                            <span style={{
                                fontFamily: 'var(--mom-font-sans)', fontSize: 11,
                                color: 'var(--mom-warm-gray)', opacity: 0.6,
                            }}>Enter to save · Esc to cancel</span>
                        </div>
                        {editError && (
                            <p style={{
                                fontFamily: 'var(--mom-font-sans)', fontSize: 11,
                                color: '#dc2626', marginTop: 6, marginBottom: 0,
                            }}>Couldn't save — please try again.</p>
                        )}
                    </div>
                ) : (
                    <p style={{
                        fontFamily: 'var(--mom-font-sans)', fontSize: isReply ? 13 : 14,
                        color: 'var(--mom-charcoal)', lineHeight: 1.6,
                        whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                        margin: 0,
                    }}>{comment.text}</p>
                )}
                {/* Reply button — top-level only, admin only, not on temps, and hidden
                    when this comment's reply input is already open. Per product spec,
                    one level of nesting (no replies to replies). */}
                {!isReply && isAdmin && !String(comment.id).startsWith('temp-') && replyingToId !== comment.id && (
                    <button
                        onClick={() => handleStartReply(comment.id)}
                        style={{
                            background: 'none', border: 'none', cursor: 'pointer',
                            padding: '4px 0', marginTop: 6,
                            fontFamily: 'var(--mom-font-sans)', fontSize: 11, fontWeight: 600,
                            color: 'var(--mom-warm-gray)',
                            opacity: 0.7, transition: 'opacity 0.15s',
                        }}
                        onMouseEnter={(e) => { e.currentTarget.style.opacity = '1'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.opacity = '0.7'; }}
                    >
                        Reply
                    </button>
                )}
                {/* Edit button — author only, active non-temp comments, hidden while
                    this row is already in edit mode. Indented to clear the Reply
                    button only when both are shown (admin editing their own top-level). */}
                {userId && comment.userId === userId && comment.status === 'active' && !String(comment.id).startsWith('temp-') && editingId !== comment.id && (
                    <button
                        onClick={() => handleStartEdit(comment)}
                        style={{
                            background: 'none', border: 'none', cursor: 'pointer',
                            padding: '4px 0', marginTop: 6,
                            marginLeft: (!isReply && isAdmin && replyingToId !== comment.id) ? 14 : 0,
                            fontFamily: 'var(--mom-font-sans)', fontSize: 11, fontWeight: 600,
                            color: 'var(--mom-warm-gray)',
                            opacity: 0.7, transition: 'opacity 0.15s',
                        }}
                        onMouseEnter={(e) => { e.currentTarget.style.opacity = '1'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.opacity = '0.7'; }}
                    >
                        Edit
                    </button>
                )}
            </div>
            {/* Delete button (admin only, not on temps) */}
            {isAdmin && !String(comment.id).startsWith('temp-') && (
                <button
                    onClick={() => handleDelete(comment)}
                    disabled={deletingId === comment.id}
                    style={{
                        background: 'none', border: 'none', cursor: 'pointer',
                        padding: '4px 8px', flexShrink: 0,
                        opacity: deletingId === comment.id ? 0.3 : 0.4,
                        transition: 'opacity 0.15s',
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.opacity = '1'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.opacity = '0.4'; }}
                    title="Delete comment"
                >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--mom-warm-gray)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="3 6 5 6 21 6" />
                        <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                    </svg>
                </button>
            )}
        </div>
    );

    // Render the inline reply input below a parent's reply chain. Auto-focused so
    // admin can type immediately. Submits on Enter; cancels on Escape.
    const renderReplyInput = (parent, isLastInBlock) => (
        <div
            key={`reply-input-${parent.id}`}
            style={{
                paddingLeft: 42, paddingTop: 4, paddingBottom: 12,
                borderBottom: isLastInBlock ? 'none' : '1px solid var(--mom-cream-dark)',
            }}
        >
            <div style={{
                fontFamily: 'var(--mom-font-sans)', fontSize: 11, color: 'var(--mom-warm-gray)',
                marginBottom: 6, display: 'flex', alignItems: 'center', gap: 8,
            }}>
                <span>Replying to {parent.isCoach ? 'Danielle' : parent.userName}</span>
                {replyJustPosted && (
                    <span style={{
                        display: 'inline-flex', alignItems: 'center', gap: 4,
                        padding: '2px 8px', borderRadius: 10,
                        background: 'color-mix(in srgb, var(--mom-primary) 12%, transparent)',
                        color: 'var(--mom-primary)',
                        fontSize: 11, fontWeight: 600,
                        animation: 'fadeIn 0.2s ease-out',
                    }}>
                        <span style={{ fontSize: 12, lineHeight: 1 }}>✓</span>
                        <span>Posted</span>
                    </span>
                )}
                <button
                    onClick={handleCancelReply}
                    style={{
                        background: 'none', border: 'none', cursor: 'pointer',
                        padding: '2px 6px', fontSize: 14, color: 'var(--mom-warm-gray)',
                        marginLeft: 'auto', lineHeight: 1,
                    }}
                    title="Close reply"
                >×</button>
            </div>
            <div style={{ display: 'flex', alignItems: 'flex-end', gap: 8 }}>
                <div style={{
                    width: 24, height: 24, borderRadius: '50%', flexShrink: 0,
                    background: 'linear-gradient(135deg, var(--mom-primary), var(--mom-primary-dark))',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                    <span style={{
                        fontFamily: 'var(--mom-font-sans)', fontSize: 10, fontWeight: 700,
                        color: '#fff',
                    }}>{userInitials}</span>
                </div>
                <div style={{ flex: 1, position: 'relative' }}>
                    <input
                        type="text"
                        value={replyText}
                        autoFocus
                        onChange={(e) => { setReplyText(e.target.value.slice(0, 280)); setReplyError(false); setReplyJustPosted(false); }}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handlePostReply(parent.id); }
                            if (e.key === 'Escape') { e.preventDefault(); handleCancelReply(); }
                        }}
                        placeholder="Reply..."
                        maxLength={280}
                        style={{
                            width: '100%', padding: '8px 40px 8px 14px',
                            background: 'var(--mom-cream)', border: 'none',
                            borderRadius: 10, fontFamily: 'var(--mom-font-sans)',
                            fontSize: 13, color: 'var(--mom-charcoal)',
                            outline: 'none', boxSizing: 'border-box',
                            transition: 'box-shadow 0.15s ease',
                        }}
                        onFocus={(e) => { e.target.style.boxShadow = '0 0 0 2px color-mix(in srgb, var(--mom-primary) 25%, transparent)'; }}
                        onBlur={(e) => { e.target.style.boxShadow = 'none'; }}
                    />
                    {replyText.trim() && (
                        <button
                            onClick={() => handlePostReply(parent.id)}
                            disabled={replyPosting}
                            style={{
                                position: 'absolute', right: 4, top: '50%', transform: 'translateY(-50%)',
                                width: 28, height: 28, borderRadius: '50%',
                                background: 'var(--mom-primary)', border: 'none',
                                cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                                opacity: replyPosting ? 0.5 : 1,
                            }}
                        >
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                <line x1="22" y1="2" x2="11" y2="13" />
                                <polygon points="22 2 15 22 11 13 2 9 22 2" />
                            </svg>
                        </button>
                    )}
                </div>
            </div>
            {replyError && (
                <p style={{
                    fontFamily: 'var(--mom-font-sans)', fontSize: 11,
                    color: '#dc2626', marginTop: 6, marginBottom: 0,
                }}>Couldn't post — please try again.</p>
            )}
        </div>
    );

    // Preview text for collapsed state — newest top-level *member* comment.
    // Excludes coach posts (admin's own comments) and replies so the teaser
    // always surfaces a real community voice, never Danielle's own words.
    // Excludes backfilled parents too: those are historical context for orphan
    // replies, not current activity, so they shouldn't drive the "what's
    // happening now" teaser.
    // Note: `displayCount` above still reflects ALL non-backfilled comments —
    // only the preview text is filtered. Subscription returns newest-first, so
    // .find() returns the most recent qualifying match.
    // Returns null when no qualifying comment exists (collapsed header just
    // shows the count, same as the empty case).
    const previewComment = comments.find((c) => !c.isCoach && !c.parentCommentId && !c._backfilled && !c._preservedForReply) || null;

    // Comment icon SVG
    const commentIcon = (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--mom-warm-gray)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
        </svg>
    );

    return (
        <div style={{ marginTop: 20 }}>
            {/* ── Collapsed teaser / header ── */}
            <button
                onClick={() => setExpanded(!expanded)}
                style={{
                    width: '100%',
                    display: 'flex', alignItems: 'center', gap: 10,
                    padding: '14px 20px',
                    background: 'rgba(255,255,255,0.55)',
                    backdropFilter: 'blur(20px) saturate(1.4)',
                    WebkitBackdropFilter: 'blur(20px) saturate(1.4)',
                    border: '1px solid rgba(255,255,255,0.55)',
                    borderRadius: expanded ? '16px 16px 0 0' : 16,
                    cursor: 'pointer',
                    transition: 'border-radius 0.2s ease',
                    boxShadow: expanded ? 'none' : 'var(--shadow-sm)',
                    textAlign: 'left',
                }}
            >
                {commentIcon}
                <span style={{
                    fontFamily: 'var(--mom-font-sans)', fontSize: 13, fontWeight: 600,
                    color: 'var(--mom-charcoal)',
                }}>
                    {displayCount > 0 ? `${displayCount} comment${displayCount !== 1 ? 's' : ''}` : 'Comments'}
                </span>
                {!expanded && previewComment && (
                    <span style={{
                        fontFamily: 'var(--mom-font-sans)', fontSize: 13,
                        color: 'var(--mom-warm-gray)',
                        flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    }}>
                        &middot; "{previewComment.text.slice(0, 60)}{previewComment.text.length > 60 ? '...' : ''}" — {previewComment.userName}
                    </span>
                )}
                <span style={{
                    fontSize: 14, color: 'var(--mom-warm-gray)',
                    transition: 'transform 0.2s ease',
                    transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
                    flexShrink: 0, marginLeft: 'auto',
                }}>
                    ▾
                </span>
            </button>

            {/* ── Expanded comment thread ── */}
            {expanded && (
                <div ref={highlightContainerRef} style={{
                    background: 'rgba(255,255,255,0.55)',
                    backdropFilter: 'blur(20px) saturate(1.4)',
                    WebkitBackdropFilter: 'blur(20px) saturate(1.4)',
                    border: '1px solid rgba(255,255,255,0.55)',
                    borderTop: 'none',
                    borderRadius: '0 0 16px 16px',
                    padding: '4px 20px 20px',
                    boxShadow: 'var(--shadow-sm)',
                    animation: 'fadeIn 0.2s ease-out',
                }}>
                    {loading ? (
                        // Structural skeleton — mirrors the comment thread layout
                        // (input bar at top + 3 comment rows below) so the user sees
                        // the shape of incoming content rather than a generic spinner.
                        // Inline rgba gradient stops give visible contrast on the
                        // glass-blurred panel background; the global mom-skeleton
                        // class is too pale for this surface.
                        // Uses the existing `shimmer` keyframes from index.css.
                        (() => {
                            const sk = (extra) => ({
                                background: 'linear-gradient(90deg, rgba(0,0,0,0.04) 25%, rgba(0,0,0,0.08) 50%, rgba(0,0,0,0.04) 75%)',
                                backgroundSize: '200% 100%',
                                animation: 'shimmer 1.5s ease infinite',
                                borderRadius: 8,
                                ...extra,
                            });
                            return (
                                <div aria-busy="true" aria-label="Loading comments" style={{ padding: '12px 0 4px' }}>
                                    {/* Input bar skeleton (avatar + text input) */}
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, paddingBottom: 12, marginBottom: 12, borderBottom: '1px solid var(--mom-cream-dark)' }}>
                                        <div style={sk({ width: 32, height: 32, borderRadius: '50%', flexShrink: 0 })} />
                                        <div style={sk({ flex: 1, height: 38, borderRadius: 12 })} />
                                    </div>
                                    {/* Comment rows skeleton — 3 rows with avatar + name line + 2 text lines */}
                                    {[0, 1, 2].map((i) => (
                                        <div key={i} style={{ display: 'flex', gap: 10, padding: '10px 0', borderBottom: i < 2 ? '1px solid rgba(0,0,0,0.04)' : 'none' }}>
                                            <div style={sk({ width: 32, height: 32, borderRadius: '50%', flexShrink: 0 })} />
                                            <div style={{ flex: 1, minWidth: 0 }}>
                                                <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                                                    <div style={sk({ width: 90, height: 11, borderRadius: 4 })} />
                                                    <div style={sk({ width: 50, height: 11, borderRadius: 4 })} />
                                                </div>
                                                <div style={sk({ height: 11, marginBottom: 6, borderRadius: 4, width: i === 0 ? '85%' : i === 1 ? '70%' : '90%' })} />
                                                <div style={sk({ height: 11, borderRadius: 4, width: i === 0 ? '60%' : i === 1 ? '40%' : '55%' })} />
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            );
                        })()
                    ) : (
                        <>
                            {/* Comment input (top — encourages posting) */}
                            {userId ? (
                                <div style={{
                                    display: 'flex', alignItems: 'flex-end', gap: 10,
                                    padding: '16px 0 12px',
                                    borderBottom: comments.length > 0 ? '1px solid var(--mom-cream-dark)' : 'none',
                                }}>
                                    {/* User avatar */}
                                    <div style={{
                                        width: 32, height: 32, borderRadius: '50%', flexShrink: 0,
                                        background: 'linear-gradient(135deg, var(--mom-primary), var(--mom-primary-dark))',
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        position: 'relative',
                                    }}>
                                        <span style={{
                                            fontFamily: 'var(--mom-font-sans)', fontSize: 12, fontWeight: 700,
                                            color: '#fff',
                                        }}>{userInitials}</span>
                                        {!isAdmin && <AvatarPhoto url={photoFor(userId)} />}
                                    </div>
                                    {/* Input + send */}
                                    <div style={{ flex: 1, position: 'relative' }}>
                                        <input
                                            type="text"
                                            value={text}
                                            onChange={(e) => { setText(e.target.value.slice(0, 280)); setPostError(false); }}
                                            onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handlePost(); } }}
                                            placeholder="Say something..."
                                            maxLength={280}
                                            style={{
                                                width: '100%', padding: '10px 44px 10px 16px',
                                                background: 'var(--mom-cream)', border: 'none',
                                                borderRadius: 12, fontFamily: 'var(--mom-font-sans)',
                                                fontSize: 14, color: 'var(--mom-charcoal)',
                                                outline: 'none', boxSizing: 'border-box',
                                                transition: 'box-shadow 0.15s ease',
                                            }}
                                            onFocus={(e) => { e.target.style.boxShadow = '0 0 0 2px color-mix(in srgb, var(--mom-primary) 25%, transparent)'; }}
                                            onBlur={(e) => { e.target.style.boxShadow = 'none'; }}
                                        />
                                        {text.trim() && (
                                            <button
                                                onClick={handlePost}
                                                disabled={posting}
                                                style={{
                                                    position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)',
                                                    width: 32, height: 32, borderRadius: '50%',
                                                    background: 'var(--mom-primary)', border: 'none',
                                                    cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                    opacity: posting ? 0.5 : 1,
                                                    transition: 'transform 0.1s ease',
                                                }}
                                                onMouseDown={(e) => { e.currentTarget.style.transform = 'translateY(-50%) scale(0.9)'; }}
                                                onMouseUp={(e) => { e.currentTarget.style.transform = 'translateY(-50%) scale(1)'; }}
                                            >
                                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                                    <line x1="22" y1="2" x2="11" y2="13" />
                                                    <polygon points="22 2 15 22 11 13 2 9 22 2" />
                                                </svg>
                                            </button>
                                        )}
                                    </div>
                                </div>
                            ) : null}

                            {/* Character count */}
                            {text.length >= 250 && (
                                <p style={{
                                    fontFamily: 'var(--mom-font-sans)', fontSize: 11,
                                    color: text.length >= 275 ? 'var(--mom-primary)' : 'var(--mom-warm-gray)',
                                    textAlign: 'right', marginTop: 4, marginBottom: 0,
                                }}>{280 - text.length} characters left</p>
                            )}

                            {/* Post error */}
                            {postError && (
                                <p style={{
                                    fontFamily: 'var(--mom-font-sans)', fontSize: 12,
                                    color: '#dc2626', marginTop: 8, marginBottom: 0,
                                }}>Couldn't post — please try again.</p>
                            )}

                            {/* ── Threaded comment list ──
                                Top-level comments render in newest-first order. Each parent's
                                replies render directly below it (oldest-first within the group)
                                with smaller avatar + "↳" indicator + 42px indent. The reply
                                input opens inline below the parent's reply chain when admin
                                clicks Reply. Orphaned replies (parent paginated off) render at
                                the end as if top-level replies — graceful degradation. */}
                            {comments.length > 0 ? (
                                <div style={{ marginTop: userId ? 0 : 12 }}>
                                    {topLevelComments.map((parent, parentIdx) => {
                                        const replies = repliesByParent[parent.id] || [];
                                        const showReplyInput = isAdmin && replyingToId === parent.id;
                                        const isLastTopLevel = parentIdx === topLevelComments.length - 1 && orphanedReplies.length === 0;
                                        const parentIsLastRow = replies.length === 0 && !showReplyInput && isLastTopLevel;
                                        return (
                                            <React.Fragment key={parent.id}>
                                                {renderRow({ comment: parent, isReply: false, isLastInGroup: parentIsLastRow })}
                                                {replies.map((reply, replyIdx) => {
                                                    const isLastReply = replyIdx === replies.length - 1;
                                                    const replyIsLastRow = isLastReply && !showReplyInput && isLastTopLevel;
                                                    return renderRow({ comment: reply, isReply: true, isLastInGroup: replyIsLastRow });
                                                })}
                                                {showReplyInput && renderReplyInput(parent, isLastTopLevel)}
                                            </React.Fragment>
                                        );
                                    })}
                                    {/* Orphan section — see grouping comment above for why this
                                        should be vanishingly rare. The divider visually disconnects
                                        these from the last loaded top-level so they can't be
                                        misread as replies to that comment (the original bug). */}
                                    {orphanedReplies.length > 0 && (
                                        <div style={{
                                            paddingTop: 16, marginTop: 4,
                                            borderTop: '1px dashed var(--mom-cream-dark)',
                                            fontFamily: 'var(--mom-font-sans)', fontSize: 11,
                                            fontStyle: 'italic', color: 'var(--mom-warm-gray)',
                                            textAlign: 'center', paddingBottom: 4,
                                        }}>
                                            Earlier conversation
                                        </div>
                                    )}
                                    {orphanedReplies.map((reply, idx) => {
                                        const isLast = idx === orphanedReplies.length - 1;
                                        return renderRow({ comment: reply, isReply: true, isLastInGroup: isLast });
                                    })}

                                    {/* Load more */}
                                    {hasMore && (
                                        <button
                                            onClick={handleLoadMore}
                                            disabled={loadingMore}
                                            style={{
                                                width: '100%', padding: '10px 0', marginTop: 8,
                                                background: 'none', border: 'none', cursor: 'pointer',
                                                fontFamily: 'var(--mom-font-sans)', fontSize: 13, fontWeight: 600,
                                                color: 'var(--mom-primary)',
                                                opacity: loadingMore ? 0.5 : 1,
                                            }}
                                        >
                                            {loadingMore ? 'Loading...' : 'Load more comments'}
                                        </button>
                                    )}
                                </div>
                            ) : (
                                /* Empty state */
                                <div style={{ textAlign: 'center', padding: '20px 0 8px' }}>
                                    <p style={{
                                        fontFamily: 'var(--mom-font-serif)', fontSize: 15, fontStyle: 'italic',
                                        color: 'var(--mom-warm-gray)',
                                    }}>
                                        {userId ? 'Be the first to share your thoughts!' : 'No comments yet.'}
                                    </p>
                                </div>
                            )}
                        </>
                    )}
                </div>
            )}
        </div>
    );
}

// ── Release switch (member replies) ─────────────────────────────────────────
// siteConfig.memberRepliesEnabled === true ⇒ ClassCommentsV2 (member replies,
// moderation, faster loading). Otherwise the component above, UNCHANGED — so
// with the switch off every viewer gets exactly today's thread. A member can
// preview V2 on her own device with ?memberReplies=preview (pre-launch testing;
// the server enforces every rule regardless). V2 hands back to the legacy
// thread by itself if its query fails (e.g. the new index isn't built yet).
function memberRepliesOn() {
    try {
        const qs = new URLSearchParams(window.location.search).get('memberReplies');
        if (qs === 'preview') localStorage.setItem('mom_member_replies_preview', '1');
        if (qs === 'off') localStorage.removeItem('mom_member_replies_preview');
        if (localStorage.getItem('mom_member_replies_preview') === '1') return true;
    } catch (_) { /* storage unavailable */ }
    return (getCachedSiteContent() || {}).memberRepliesEnabled === true;
}

function ClassComments(props) {
    const [useV2, setUseV2] = useState(memberRepliesOn);
    if (useV2) return <ClassCommentsV2 {...props} onFallback={() => setUseV2(false)} />;
    return <ClassCommentsLegacy {...props} />;
}

export default ClassComments;
